// src/pages/Chat.jsx
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLang } from '../lib/i18n.jsx';
import QuickExit from '../components/QuickExit.jsx';
import {
  startCase, sendMessage, pollForStaffMessages, saveResumePin, resumeCase, notifyTyping,
  subscribeToCaseEvents, uploadAttachment, fetchTranscript,
} from '../lib/chatApi.js';

// localStorage with an explicit 30-minute expiry — deliberately different
// from the earlier sessionStorage choice. sessionStorage already survives
// refresh (it's tied to the tab, not the page load) but is wiped the
// instant the tab/window closes; this persists across closing and
// reopening the browser too, for a bounded window, per product decision.
// QuickExit clears this immediately, so a deliberate fast exit on a shared
// device doesn't leave a resumable trail for the rest of the window.
const SESSION_TTL_MS = 30 * 60 * 1000;
const STORAGE_KEY = 'sauti_session';

function useCaseSession() {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { caseId: null, caseRef: null };
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.savedAt > SESSION_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return { caseId: null, caseRef: null };
      }
      return { caseId: parsed.caseId, caseRef: parsed.caseRef };
    } catch {
      return { caseId: null, caseRef: null };
    }
  });

  const set = (id, ref) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ caseId: id, caseRef: ref, savedAt: Date.now() }));
    setState({ caseId: id, caseRef: ref });
  };

  return { caseId: state.caseId, caseRef: state.caseRef, set };
}

function speak(text, lang) {
  if (!('speechSynthesis' in window) || !text) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang === 'sw' ? 'sw-KE' : 'en-US';
  window.speechSynthesis.speak(utterance);
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Groups consecutive same-sender messages so only the last one in a run gets a tail + timestamp — mirrors how people actually read a conversation, not a wall of repeated chrome. */
function groupMessages(messages) {
  return messages.map((m, i) => {
    const next = messages[i + 1];
    const isLastInGroup = !next || next.role !== m.role;
    return { ...m, isLastInGroup };
  });
}

export default function Chat() {
  const { t, lang } = useLang();
  const session = useCaseSession();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [humanMode, setHumanMode] = useState(false);
  const [staffName, setStaffName] = useState(null);
  const [staffOnline, setStaffOnline] = useState(false);
  const [staffTyping, setStaffTyping] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [recording, setRecording] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeout = useRef(null);
  const staffTypingTimeout = useRef(null);
  const lastPolledId = useRef(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    if (session.caseId) {
      fetchTranscript(session.caseId).then((body) => {
        setRestoring(false);
        if (!body) return;
        setMessages(body.messages.map((m) => ({ role: m.role, content: m.content, id: m.id, createdAt: m.createdAt })));
        setHumanMode(body.humanMode);
        setStaffName(body.staffName);
        setStaffOnline(body.staffOnline ?? false);
        const staffIds = body.messages.filter((m) => m.role === 'staff').map((m) => m.id);
        if (staffIds.length) lastPolledId.current = Math.max(...staffIds);
      });
      return;
    }
    setRestoring(false);
    startCase(lang).then(({ caseId, caseRef }) => session.set(caseId, caseRef)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!session.caseId) return;
    const unsubscribe = subscribeToCaseEvents(session.caseId, {
      onTypingStaff: () => {
        setStaffTyping(true);
        clearTimeout(staffTypingTimeout.current);
        staffTypingTimeout.current = setTimeout(() => setStaffTyping(false), 3000);
      },
      onSeen: () => {
        setMessages((prev) => prev.map((m) => (m.role === 'user' ? { ...m, seen: true, delivered: true } : m)));
      },
    });
    return unsubscribe;
  }, [session.caseId]);

  useEffect(() => {
    if (!session.caseId) return;
    const iv = setInterval(async () => {
      const body = await pollForStaffMessages(session.caseId, lastPolledId.current);
      if (!body) return;
      setHumanMode(body.humanMode);
      setStaffName(body.staffName);
      setStaffOnline(body.staffOnline ?? false);
      if (body.messages?.length) {
        setMessages((prev) => [...prev, ...body.messages.map((m) => ({ role: 'staff', content: m.content, id: m.id, createdAt: m.createdAt }))]);
        lastPolledId.current = Math.max(lastPolledId.current, ...body.messages.map((m) => m.id));
      }
      if (body.receipts?.length) {
        setMessages((prev) => {
          let userIndex = 0;
          return prev.map((m) => {
            if (m.role !== 'user') return m;
            const receipt = body.receipts[userIndex];
            userIndex++;
            return receipt ? { ...m, delivered: receipt.delivered, seen: receipt.seen } : m;
          });
        });
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [session.caseId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, staffTyping]);

  // Auto-grow the textarea up to ~5 lines, then scroll internally.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  const handleSend = useCallback(async (e) => {
    e.preventDefault();
    if (!input.trim() || !session.caseId || sending) return;
    const userMsg = { role: 'user', content: input.trim(), createdAt: new Date().toISOString() };
    const history = [...messages, userMsg].map(({ role, content }) => ({ role, content }));
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    let assistantText = '';
    setMessages((prev) => [...prev, { role: 'assistant', content: '', createdAt: new Date().toISOString() }]);

    await sendMessage(session.caseId, history, {
      onToken: (text) => {
        assistantText += text;
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: assistantText };
          return copy;
        });
      },
      onHumanAck: (name) => {
        setHumanMode(true);
        setStaffName(name);
        setMessages((prev) => prev.slice(0, -1));
      },
      onError: () => {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: "I'm having trouble connecting. Please try again in a moment." };
          return copy;
        });
      },
    });

    setSending(false);
  }, [input, messages, session.caseId, sending]);

  function handleTyping(e) {
    setInput(e.target.value);
    if (typingTimeout.current) return;
    notifyTyping(session.caseId);
    typingTimeout.current = setTimeout(() => { typingTimeout.current = null; }, 3000);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], 'voice-note.webm', { type: 'audio/webm' });
        setMessages((prev) => [...prev, { role: 'user', content: '🎤 Voice message', isVoice: true, createdAt: new Date().toISOString() }]);
        try {
          await uploadAttachment(session.caseId, file);
        } catch {
          setMessages((prev) => [...prev, { role: 'assistant', content: 'Could not send that voice note — please try again.', createdAt: new Date().toISOString() }]);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: "I couldn't access your microphone. Check your browser's permission settings, or just type instead.", createdAt: new Date().toISOString() }]);
    }
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  }

  const grouped = groupMessages(messages);

  return (
    <div
      className="min-h-screen flex flex-col bg-cream"
      style={{ backgroundImage: 'radial-gradient(circle, rgba(44,42,38,0.045) 1px, transparent 1px)', backgroundSize: '18px 18px' }}
    >
      <QuickExit />
      <header
        className="flex items-center justify-between px-4 py-3 pr-24 border-b border-ink/10 bg-white/90 backdrop-blur-sm gap-3 sticky top-0 z-20 shadow-sm"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="h-8 w-8 rounded-full bg-sage flex items-center justify-center text-white font-display font-semibold text-sm">S</div>
          <div className="font-display font-semibold text-sage-600">Sauti</div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {humanMode && staffName && (
            <span className="flex items-center gap-1.5 text-xs text-ink/60 shrink-0">
              <span className={`h-1.5 w-1.5 rounded-full ${staffOnline ? 'bg-sage' : 'bg-ink/20'}`} />
              <span className="hidden sm:inline">{staffOnline ? 'Online' : 'Away'}</span>
            </span>
          )}
          {session.caseId && (
            <CaseRefControl
              caseId={session.caseId} caseRef={session.caseRef}
              onOpenResume={() => setShowResume(true)} t={t}
            />
          )}
        </div>
      </header>

      {humanMode && (
        <div className="bg-sage-100 text-sage-600 text-xs text-center py-1.5">
          You're now talking with {staffName ?? 'a member of our team'}.
        </div>
      )}

      <main className="flex-1 overflow-y-auto px-4 py-4 max-w-lg mx-auto w-full" aria-live="polite">
        {restoring && (
          <div className="space-y-3 animate-pulse">
            <div className="h-14 w-2/3 bg-white/70 rounded-2xl" />
            <div className="h-10 w-1/2 bg-white/70 rounded-2xl ml-auto" />
          </div>
        )}
        {!restoring && messages.length === 0 && (
          <p className="text-center text-sm text-ink/50 mt-8">You can start typing whenever you're ready.</p>
        )}
        {grouped.map((m, i) => (
          <Bubble key={m.id ?? i} m={m} isLast={m.isLastInGroup} sending={sending && i === grouped.length - 1} lang={lang} />
        ))}
        {staffTyping && <TypingDots />}
        <div ref={bottomRef} />
      </main>

      <form
        onSubmit={handleSend}
        className="px-4 pt-3 border-t border-ink/10 bg-white flex items-end gap-2 max-w-lg mx-auto w-full"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <textarea
          ref={textareaRef}
          value={input} onChange={handleTyping} onKeyDown={handleKeyDown}
          placeholder={t.chatPlaceholder}
          rows={1}
          className="flex-1 resize-none rounded-2xl border border-ink/20 px-4 py-2.5 text-base leading-snug focus:border-sage focus:ring-1 focus:ring-sage max-h-[120px]"
        />
        <button
          type="button"
          onClick={recording ? stopRecording : startRecording}
          aria-label={recording ? 'Stop recording' : 'Record a voice message'}
          className={`relative shrink-0 rounded-full text-white text-sm h-11 w-11 flex items-center justify-center transition-colors touch-manipulation ${recording ? 'bg-coral' : 'bg-ink/70 hover:bg-ink'}`}
          title={recording ? 'Stop recording' : 'Record a voice message'}
        >
          {recording && <span className="absolute inset-0 rounded-full bg-coral animate-ping opacity-60" />}
          <span className="relative">{recording ? '■' : '🎤'}</span>
        </button>
        <button
          type="submit" disabled={sending || !input.trim()}
          aria-label={t.send}
          className="shrink-0 rounded-full bg-sage hover:bg-sage-600 text-white h-11 w-11 flex items-center justify-center disabled:opacity-40 transition-colors touch-manipulation"
        >
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path d="M2 10l15-7-4 7 4 7-15-7z" /></svg>
        </button>
      </form>

      {showResume && (
        <ResumeModal
          onResumed={(id, ref, msgs) => { session.set(id, ref); setMessages(msgs); setShowResume(false); }}
          onClose={() => setShowResume(false)}
          t={t}
        />
      )}
    </div>
  );
}

function Bubble({ m, isLast, sending, lang }) {
  const isUser = m.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${isLast ? 'mb-3' : 'mb-1'} animate-rise`}>
      <div className="max-w-[80%]">
        <div
          className={`relative px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
            isUser
              ? `bg-sage text-white rounded-2xl ${isLast ? 'rounded-br-md' : ''}`
              : `bg-white border border-ink/10 text-ink rounded-2xl ${isLast ? 'rounded-bl-md' : ''}`
          }`}
        >
          {m.content || (sending ? <TypingDotsInline /> : '')}
          {!isUser && m.content && (
            <button
              onClick={() => speak(m.content, lang)}
              aria-label="Listen to this message"
              className="ml-2 text-ink/30 hover:text-sage align-middle"
              title="Listen"
            >
              🔊
            </button>
          )}
        </div>
        {isLast && (
          <div className={`flex items-center gap-1 mt-1 px-1 text-[10px] text-ink/35 ${isUser ? 'justify-end' : 'justify-start'}`}>
            {m.createdAt && <span>{formatTime(m.createdAt)}</span>}
            {isUser && (m.delivered || m.seen) && <ReadTicks seen={m.seen} />}
          </div>
        )}
      </div>
    </div>
  );
}

/** Branded double-check receipt — same idea as WhatsApp's ticks, own color language: grey = delivered, sage = seen. */
function ReadTicks({ seen }) {
  return (
    <svg width="14" height="10" viewBox="0 0 16 11" fill="none" className={seen ? 'text-sage' : 'text-ink/30'}>
      <path d="M1 5.5L4.5 9L11 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 5.5L9 9L15.5 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TypingDots() {
  return (
    <div className="flex justify-start mb-3 animate-rise">
      <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white border border-ink/10 shadow-sm">
        <TypingDotsInline />
      </div>
    </div>
  );
}

function TypingDotsInline() {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-1.5 w-1.5 rounded-full bg-ink/30 animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="h-1.5 w-1.5 rounded-full bg-ink/30 animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="h-1.5 w-1.5 rounded-full bg-ink/30 animate-bounce" style={{ animationDelay: '300ms' }} />
    </span>
  );
}

function CaseRefControl({ caseId, caseRef, onOpenResume, t }) {
  const [mode, setMode] = useState('idle'); // idle | saving | saved
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    try {
      await saveResumePin(caseId, pin);
      setMode('saved');
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }

  if (mode === 'saving') {
    return (
      <form onSubmit={submit} className="flex items-center gap-1.5 shrink-0">
        <input
          value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric" placeholder="••••" aria-label="4-digit PIN" autoFocus
          className="w-14 text-center tracking-[0.3em] rounded-md border border-ink/20 px-1.5 py-1 text-xs font-mono"
        />
        <button type="submit" className="text-xs text-sage-600 font-medium hover:underline">Save</button>
        <button type="button" onClick={() => setMode('idle')} className="text-xs text-ink/40 hover:text-ink">✕</button>
        {error && <span className="text-[10px] text-coral-600">{error}</span>}
      </form>
    );
  }

  return (
    <div className="flex items-center gap-2 shrink-0 text-xs">
      <button onClick={onOpenResume} className="text-ink/50 hover:text-ink font-mono" title="Resume on another device">
        {caseRef}
      </button>
      {mode === 'saved' ? (
        <span className="text-sage-600">✓ Saved</span>
      ) : (
        <button onClick={() => setMode('saving')} className="text-sage-600 hover:underline font-medium" title={t.savePinPrompt}>
          Save
        </button>
      )}
    </div>
  );
}

function ResumeModal({ onResumed, onClose, t }) {
  const [caseRef, setCaseRef] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  async function submit(e) {
    e.preventDefault();
    try {
      const body = await resumeCase(caseRef, pin);
      onResumed(body.caseId, caseRef.toUpperCase(), body.messages.map((m) => ({ role: m.role, content: m.content })));
    } catch (err) {
      setError(err.message);
    }
  }
  return (
    <div className="fixed inset-0 bg-ink/40 flex items-end sm:items-center justify-center p-4 z-40">
      <form onSubmit={submit} className="bg-white rounded-xl p-5 max-w-xs w-full space-y-3">
        <p className="text-sm font-medium">{t.resumeTitle}</p>
        <input value={caseRef} onChange={(e) => setCaseRef(e.target.value)} placeholder="SAU-2026-0001" className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm font-mono" />
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" placeholder="PIN" className="w-full text-center tracking-[0.5em] rounded-md border border-ink/20 px-3 py-2 font-mono" />
        {error && <p className="text-xs text-coral-600">{error}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 text-xs text-ink/50 py-2">Cancel</button>
          <button type="submit" className="flex-1 rounded-md bg-sage text-white text-xs font-medium py-2">{t.resumeButton}</button>
        </div>
      </form>
    </div>
  );
}
